import type { DocumentReference as FhirDocumentReference } from '@medplum/fhirtypes';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '../ui/skeleton';
import { DocumentReference } from './DocumentReference';

export type SpecimenLabelSectionProps = {
  documents: FhirDocumentReference[] | undefined;
  isLoading: boolean;
};

/**
 * Renders Health Gorilla `SpecimenLabel` `DocumentReference` PDFs alongside a lab
 * order. Looked up by the same HG requisition identifier as
 * `RequisitionDocumentsSection`, just with a different `category`.
 *
 * Despite the name, this is not the FHIR `Specimen` resource — it's a printable
 * label PDF that the lab emits.
 */
export const SpecimenLabelSection = ({ documents, isLoading }: SpecimenLabelSectionProps): React.ReactElement => {
  const { t } = useTranslation('records');

  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold">{t('specimenLabel')}</h2>
      {isLoading && <Skeleton className="h-[600px] w-full" />}
      {!isLoading && (!documents || documents.length === 0) && (
        <p className="text-sm text-muted-foreground">{t('noSpecimenLabel')}</p>
      )}
      {!isLoading && documents?.map((doc, idx) => <DocumentReference key={doc.id ?? idx} documentReference={doc} />)}
    </section>
  );
};
